<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12" md="4" lg="3">
          <v-expansion-panels v-model="infoPanel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="font-weight-bold">
                Job Card Info
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form class="pt-4">
                  <v-date-input
                    v-model="form.job_card_date"
                    label="Job Card Date *"
                    input-format="dd/mm/yyyy"
                    prepend-icon=""
                    prepend-inner-icon="mdi-calendar-month-outline"
                    variant="outlined"
                    density="compact"
                    bg-color="grey-lighten-4"
                    :error-messages="errors.job_card_date"
                  />
                  <v-select
                    v-model="form.status"
                    :items="statuses"
                    label="Status *"
                    variant="outlined"
                    density="compact"
                    bg-color="grey-lighten-4"
                  />
                  <v-textarea
                    v-model="form.remarks"
                    label="Remarks"
                    variant="outlined"
                    density="compact"
                    bg-color="grey-lighten-4"
                    rows="3"
                    auto-grow
                  />
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-col cols="12" md="8" lg="9">
          <v-expansion-panels v-model="orderPanel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="font-weight-bold">
                Sales Order & Product Details
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="pt-4" align="start">
                  <v-col cols="12" md="9">
                  <v-autocomplete
                    v-model="selectedOrderId"
                    :items="availableOrders"
                    :item-title="orderTitle"
                    item-value="id"
                    label="Select sales order"
                    variant="outlined"
                    density="compact"
                    bg-color="grey-lighten-4"
                    :loading="loadingOrders"
                    clearable
                  />
                  </v-col>
                  <v-col cols="12" md="3">
                  <v-btn
                    block
                    color="primary"
                    variant="tonal"
                    prepend-icon="mdi-plus"
                    :disabled="!selectedOrderId"
                    @click="addOrder"
                  >
                    Add Order
                  </v-btn>
                  </v-col>
                </v-row>

                <v-alert v-if="selectedOrder" color="primary" variant="tonal" density="compact" class="mb-4">
                  <div class="d-flex flex-wrap align-center justify-space-between ga-2">
                    <div>
                      <strong>{{ selectedOrder.code || `SO-${selectedOrder.id}` }}</strong>
                      <span class="mx-2">•</span>{{ customerName(selectedOrder) }}
                      <span class="mx-2">•</span>{{ selectedOrder.approximate_delivery_date || 'No delivery date' }}
                    </div>
                    <v-btn size="small" variant="text" color="error" prepend-icon="mdi-close" @click="removeOrder">
                      Remove
                    </v-btn>
                  </div>
                </v-alert>

              <v-alert
                v-if="errors.items"
                type="error"
                variant="tonal"
                density="compact"
                class="mb-4"
              >
                {{ errors.items }}
              </v-alert>

              <v-table density="compact" class="table-dotted-border-bottom">
                <thead>
                  <tr>
                    <th class="text-center">#</th>
                    <th>Product Name [Code]</th>
                    <th>Product Attribute</th>
                    <th>Unit</th>
                    <th class="text-end">Order Qty</th>
                    <th style="min-width: 150px">Job Card Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!jobCardItems.length">
                    <td colspan="6" class="text-center text-medium-emphasis py-10">
                      Select a sales order to load its product details.
                    </td>
                  </tr>
                  <tr v-for="(item, index) in jobCardItems" :key="item.id || index">
                    <td class="text-center">{{ index + 1 }}</td>
                    <td>
                      <div class="font-weight-medium">{{ productName(item) }}</div>
                      <div class="text-caption text-medium-emphasis">{{ productCode(item) }}</div>
                    </td>
                    <td>{{ productAttribute(item) }}</td>
                    <td>{{ productUnit(item) }}</td>
                    <td class="text-end">{{ item.order_quantity }}</td>
                    <td>
                      <v-text-field
                        v-model.number="item.job_card_quantity"
                        type="number"
                        min="0"
                        :max="item.order_quantity"
                        variant="outlined"
                        density="compact"
                        hide-details
                        bg-color="grey-lighten-4"
                      />
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="jobCardItems.length">
                  <tr>
                    <td colspan="4" class="text-end font-weight-bold py-4">Total Quantity</td>
                    <td class="text-end font-weight-bold">{{ totalOrderQuantity }}</td>
                    <td class="font-weight-bold">{{ totalJobCardQuantity }}</td>
                  </tr>
                </tfoot>
              </v-table>

              <v-divider class="my-4" />
              <div class="d-flex justify-end">
                <v-btn
                  color="primary"
                  variant="flat"
                  prepend-icon="mdi-content-save-outline"
                  :loading="saving"
                  @click="saveJobCard"
                >
                  Save Job Card
                </v-btn>
              </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const store = useStore();
const loadingOrders = ref(false);
const saving = ref(false);
const infoPanel = ref(0);
const orderPanel = ref(0);
const selectedOrderId = ref(null);
const selectedOrder = ref(null);
const jobCardItems = ref([]);

const form = reactive({
  job_card_date: new Date(),
  status: "Active",
  remarks: "",
});

const errors = reactive({ job_card_date: "", items: "" });
const statuses = ["Active", "Draft"];
const saleOrders = computed(() => store.getters.getSaleOrderList || []);
const availableOrders = computed(() => saleOrders.value.filter((order) => order.id !== selectedOrder.value?.id));
const totalOrderQuantity = computed(() => jobCardItems.value.reduce((sum, item) => sum + Number(item.order_quantity || 0), 0));
const totalJobCardQuantity = computed(() => jobCardItems.value.reduce((sum, item) => sum + Number(item.job_card_quantity || 0), 0));

function orderTitle(order) {
  return [order.code || `SO-${order.id}`, customerName(order)].filter(Boolean).join(" — ");
}

function customerName(order) {
  return order.customer?.company_name || order.customer?.company_owner_name || order.customer_name || "-";
}

function productName(item) {
  return item.product?.name || item.product_name || "Product";
}

function productCode(item) {
  return item.product?.code || item.product?.product_code || item.product_code || "-";
}

function productUnit(item) {
  return item.product?.unit?.name || item.unit?.name || item.unit_name || "-";
}

function productAttribute(item) {
  const attributes = item.attributes || item.product_attributes || [];
  if (Array.isArray(attributes)) return attributes.map((attribute) => attribute.name || attribute.value).filter(Boolean).join(", ") || "-";
  return attributes || "-";
}

function addOrder() {
  const order = saleOrders.value.find((item) => item.id === selectedOrderId.value);
  if (!order) return;

  selectedOrder.value = order;
  jobCardItems.value = (order.details || order.items || []).map((item, index) => ({
    ...item,
    id: item.id || `${order.id}-${index}`,
    order_quantity: Number(item.quantity ?? item.qty ?? item.order_quantity ?? 0),
    job_card_quantity: Number(item.remaining_quantity ?? item.quantity ?? item.qty ?? item.order_quantity ?? 0),
  }));
  selectedOrderId.value = null;
  errors.items = "";
}

function removeOrder() {
  selectedOrder.value = null;
  jobCardItems.value = [];
}

function validate() {
  errors.job_card_date = form.job_card_date ? "" : "Job card date is required";
  errors.items = jobCardItems.value.length ? "" : "Add a sales order with at least one product.";

  const hasInvalidQuantity = jobCardItems.value.some((item) => {
    const quantity = Number(item.job_card_quantity);
    return quantity <= 0 || quantity > Number(item.order_quantity);
  });
  if (hasInvalidQuantity) errors.items = "Each job card quantity must be greater than zero and cannot exceed the order quantity.";

  return !Object.values(errors).some(Boolean);
}

async function saveJobCard() {
  if (!validate()) return;
  saving.value = true;
  await new Promise((resolve) => setTimeout(resolve, 350));
  saving.value = false;
  toast.success("Job card is ready to save. Connect the job-card API to persist this form.");
}

onMounted(async () => {
  loadingOrders.value = true;
  await Promise.allSettled([store.dispatch("getSaleOrderList", {})]);
  loadingOrders.value = false;
});
</script>
